import type {ReactElement, ReactNode} from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';
import styles from './ai-chat-window.module.scss';
import {CircularProgressLoader, LOADER_COLOR} from '@Components/circular-progress-loader';
import '@Global/create-from-scratch';
import type {ActiveSurvey, AiChatWindowProps, ChatMessage, ChatMessagePart, ChatOnLoadParams, ImagePart, MessagesResponse, PendingSurvey, RequestConfig, TextPartPurpose} from './ai-chat-window.types';
import {CHAT_NOT_FOUND_ERROR_CODE} from './ai-chat-window.types';
import {useChatStream} from '@Components/ai-chat-window/hooks/useChatStream';
import {useChatOnLoadFill} from '@Components/ai-chat-window/hooks/useChatOnLoadFill';
import {
  buildRequestConfig,
  buildRequestPreferences,
  convertMessagesArrayToHashmap,
  buildEditResendParts,
  createUserRequestParts,
  findLastUserMessage,
  getAiChatCompletedStateForInProgressChat,
  withNextSuggestions,
} from '@Components/ai-chat-window/lib/ai-chat-messages.library';
import {isUserPremium, isUserPremiumPlus, isUserTrial} from '@Libraries/user.library';
import type {AiPromptBoxAttachments} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {convertImagePartToPromptAttachment} from '@Components/ai-prompt-box/libraries/ai-prompt-box-attachment.library';
import {aiPromptBoxActions} from '@Components/ai-prompt-box/ai-prompt-box.reducer';
import {PromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {parseAspectRatio} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-aspect-ratio-selector/ai-prompt-box-aspect-ratio-selector.library';
import ChatMessages from '@Components/ai-chat-window/components/chat-messages-display/chat-messages';
import {AiChatWindowPromptBox} from '@Components/ai-chat-window/components/ai-chat-window-prompt-box/ai-chat-window-prompt-box';
import {Survey} from '@Components/ai-chat-window/components/survey/survey';
import type {SurveyResponse, SurveySpec} from '@Components/ai-chat-window/components/survey/survey.types';
import {answerGivesUpPinnedAspectRatio, buildAnsweredFormSteps, buildSurveyPicks, renderSurveyResponseEcho} from '@Components/ai-chat-window/components/survey/survey.library';
import {ASPECT_RATIO_SURVEY_ID} from '@Components/ai-chat-window/components/survey/survey.types';
import {
  getAiPromptBoxStateFromStore,
  handleChatNotFound,
  handleConnectionIssue,
  getAiChatMessageCost,
  openAiChatAuthenticateModal,
  showAiChatPremiumOnlyFeatureDialog,
  showConvertTrialForAiChat,
  showPurchaseCreditsDialog,
} from '@Libraries/ai-chat.library';
import {useDispatch, useSelector} from 'react-redux';
import {useAppSelector} from '@/hooks';
import type {RootState} from '@/store';
import {aiChatWindowActions} from '@Components/ai-chat-window/ai-chat-window.reducer';
import {getTeamCredits, refreshTeamCreditsInStore} from '@Components/team-credits/team-credits-library';
import {scrollToBottom, scrollToBottomNextFrame} from '@Utils/dom.util';
import {AiChatStartSource, GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import {MESSAGE_ID_PARAM} from '@AiChat/lib/chat-history-library';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {useAiChatCallbacks} from '@AiChat/callbacks/ai-chat-callbacks.context';
import {useAiChatSize} from '@AiChat/hooks/useAiChatSize';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';
import {serializeUrl} from '@Utils/url.util';

export function AiChatWindow({currentlyOpenedChatId, onChatStart, ...props}: AiChatWindowProps): ReactElement {
  const {state: aiChatState, actions} = useAiChat();
  const callbacks = useAiChatCallbacks();
  const {isSmall, isMedium} = useAiChatSize();
  const {registerDisconnectChatStream} = actions;
  const dispatch = useDispatch();

  const {
    initialPrompt,
    initialAttachments,
    initialPinnedAspectRatio: onLoadPinnedAspectRatio,
    initialPinnedBrandKitId: onLoadPinnedBrandKitId,
  } = useChatOnLoadFill(props.chatOnLoadParams);

  const [chatLoading, setChatLoading] = useState(false);
  const [historyFetchComplete, setHistoryFetchComplete] = useState(false);
  const hasInitiatedSendOnLoadRef = useRef<boolean>(false);
  const replaceHistoryOnChatCreationRef = useRef<boolean>(false);
  const [editSendInProgressMessageKeyMap, setEditSendInProgressMessageKeyMap] = useState<Record<string, string | null>>({});
  const editSendInProgressMessageKey = currentlyOpenedChatId ? (editSendInProgressMessageKeyMap[currentlyOpenedChatId] ?? null) : null;
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const promptBoxRef = useRef<HTMLDivElement | null>(null);
  const [promptBoxHeight, setPromptBoxHeight] = useState(0);
  const pinnedAspectRatioForChatBeingCreatedRef = useRef<string | null>(null);
  const pinnedBrandKitIdForChatBeingCreatedRef = useRef<string | null>(null);
  const pinnedAspectRatio = useAppSelector((state) => state.aiPromptBox[props.id]?.pinnedAspectRatio ?? null);
  const pinnedBrandKitId = useAppSelector((state) => state.aiPromptBox[props.id]?.pinnedBrandKitId ?? null);
  const [activeSurveyByChatId, setActiveSurveyByChatId] = useState<Record<string, ActiveSurvey>>({});
  const activeSurvey = currentlyOpenedChatId ? (activeSurveyByChatId[currentlyOpenedChatId] ?? null) : null;
  const isPremiumUser = isUserPremium() || isUserPremiumPlus();

  useEffect(() => {
    if (currentlyOpenedChatId) {
      pinnedAspectRatioForChatBeingCreatedRef.current = null;
      pinnedBrandKitIdForChatBeingCreatedRef.current = null;
    }
  }, [currentlyOpenedChatId]);

  const clearSurveyForChat = useCallback((chatId: string): void => {
    setActiveSurveyByChatId((prev) => {
      const {[chatId]: _removed, ...rest} = prev;
      return rest;
    });
  }, []);

  const handleSurveyRequested = useCallback((chatId: string, spec: SurveySpec): void => {
    setActiveSurveyByChatId((prev) => ({...prev, [chatId]: {spec}}));
  }, []);

  const handleEditMessageApplied = useCallback((): void => {
    if (currentlyOpenedChatId) {
      setEditSendInProgressMessageKeyMap((prev) => ({...prev, [currentlyOpenedChatId]: null}));
    }
    actions.setEditingUserMessageId(null);
  }, [currentlyOpenedChatId, actions]);

  const handleChatCreated = useCallback((chatId: string): void => {
    const replaceHistory = replaceHistoryOnChatCreationRef.current;
    replaceHistoryOnChatCreationRef.current = false;
    props.setCurrentChatId?.(chatId, {replaceHistory});
    callbacks.onChatCreated?.(chatId);
  }, [props.setCurrentChatId, callbacks]);

  const unpinAspectRatio = useCallback((): void => {
    dispatch(aiPromptBoxActions.updateAiPromptBox({id: props.id, value: {pinnedAspectRatio: null}}));
  }, [dispatch, props.id]);

  /**
   * A generation reported that it did not use the pinned ratio. Read the pin fresh rather than
   * from render scope: the user may have pinned a new size while the design was still running,
   * and that newer choice must survive the older generation's report.
   */
  const handleAspectRatioPinDropped = useCallback(
    (droppedAspectRatio: string): void => {
      if (parseAspectRatio(getAiPromptBoxStateFromStore(props.id)?.pinnedAspectRatio) !== parseAspectRatio(droppedAspectRatio)) {
        return;
      }

      unpinAspectRatio();
    },
    [props.id, unpinAspectRatio]
  );

  const {setMessages, startGenerationStream, stopGeneration, messageSessionUids, setIsRefreshing, setHasWriteAccess, disconnect} = useChatStream({
    onChatStart,
    modeSpecificProps: props.modeSpecificProps,
    refreshChatHistory: props.refreshChatHistory,
    setCurrentChatId: handleChatCreated,
    refreshCredits: props.refreshCredits,
    chatMessagesContainerRef: messagesRef,
    onImageStreamComplete: (messages: Record<string, ChatMessage>) => {
      void props.onChatComplete?.(messages);
    },
    onUpdateHistoryItem: actions.updateChatHistoryItem,
    onSetSearchTags: actions.setSearchTagsForChat,
    onNewChat: props.onNewChat,
    onEditMessageApplied: handleEditMessageApplied,
    mode: props.mode,
    surfaceBias: props.surfaceBias,
    onSurveyRequested: handleSurveyRequested,
    onAspectRatioPinDropped: handleAspectRatioPinDropped,
  });

  const clearPinnedAspectRatioOnSurveyAnswer = useCallback(
    (surveyId: string, response: SurveyResponse | null): void => {
      if (surveyId !== ASPECT_RATIO_SURVEY_ID || !answerGivesUpPinnedAspectRatio(response)) {
        return;
      }

      unpinAspectRatio();
    },
    [unpinAspectRatio]
  );

  const raisePendingSurvey = useCallback((chatId: string, pendingSurvey: PendingSurvey): void => {
    const {messageSessionUid, ...spec} = pendingSurvey;
    messageSessionUids.set(chatId, messageSessionUid);
    handleSurveyRequested(chatId, spec);
  }, [messageSessionUids, handleSurveyRequested]);

  const abandonPendingSurvey = useCallback((chatId: string): void => {
    const messageSessionUid = messageSessionUids.get(chatId);
    if (!messageSessionUid) {
      return;
    }

    window.PMW.writeLocal('marketingagent/cancelSurvey', {chatId, messageSessionUid}).catch((e: unknown) => {
      console.error('Failed to dismiss pending survey. Details:', e);
    });
  }, [messageSessionUids]);

  const handleSurveyCancel = useCallback((): void => {
    if (!currentlyOpenedChatId) {
      return;
    }
    const dismissedSurveyId = activeSurveyByChatId[currentlyOpenedChatId]?.spec.surveyId;
    clearSurveyForChat(currentlyOpenedChatId);
    abandonPendingSurvey(currentlyOpenedChatId);

    if (dismissedSurveyId) {
      clearPinnedAspectRatioOnSurveyAnswer(dismissedSurveyId, null);
    }
  }, [abandonPendingSurvey, activeSurveyByChatId, clearPinnedAspectRatioOnSurveyAnswer, clearSurveyForChat, currentlyOpenedChatId]);

  const {messages, isStreaming, isRefreshing, hasWriteAccess, isDeleted} = useSelector((state: RootState) => {
    if (!currentlyOpenedChatId) {
      return {messages: {}, isStreaming: false, isRefreshing: false, error: null, hasWriteAccess: true, isDeleted: false};
    }

    const chatState = state.aiChatWindow[currentlyOpenedChatId];
    return {
      messages: chatState?.messages ?? {},
      isStreaming: chatState?.isStreaming ?? false,
      isRefreshing: chatState?.isRefreshing ?? false,
      error: chatState?.error ?? null,
      hasWriteAccess: chatState?.hasWriteAccess ?? true,
      isDeleted: chatState?.isDeleted ?? false,
    };
  });

  useEffect(() => {
    registerDisconnectChatStream(disconnect);
  }, [disconnect, registerDisconnectChatStream]);

  const handleRefreshedInProgressChat = useCallback(async (activeMessageSessionUid: string): Promise<void> => {
    if (!currentlyOpenedChatId) {
      return;
    }

    setIsRefreshing(currentlyOpenedChatId, true);

    const chatCompletedStateForInProgressChatResponse = await getAiChatCompletedStateForInProgressChat(currentlyOpenedChatId, activeMessageSessionUid);

    if (chatCompletedStateForInProgressChatResponse) {
      props.refreshCredits();

      const refreshedMessages = convertMessagesArrayToHashmap(chatCompletedStateForInProgressChatResponse.history);
      setMessages(chatCompletedStateForInProgressChatResponse.idchat, refreshedMessages);

      if (chatCompletedStateForInProgressChatResponse.pendingSurvey) {
        raisePendingSurvey(chatCompletedStateForInProgressChatResponse.idchat, chatCompletedStateForInProgressChatResponse.pendingSurvey);
      }

      await props.onChatComplete?.(refreshedMessages);
    }
  }, [currentlyOpenedChatId, setIsRefreshing, setMessages, raisePendingSurvey, props.refreshCredits, props.onChatComplete]);

  const getChatHistory = useCallback(async (): Promise<void> => {
    if (isStreaming) {
      return;
    }

    if (!currentlyOpenedChatId) {
      return;
    }

    try {
      setChatLoading(true);
      const response = (await window.PMW.readLocal('ai/messages', {chatId: currentlyOpenedChatId})) as MessagesResponse;
      setMessages(currentlyOpenedChatId, withNextSuggestions(convertMessagesArrayToHashmap(response.history), response.nextSuggestionButtons));
      setChatLoading(false);
      scrollToBottomNextFrame(messagesRef, 'instant');

      if (response.activeMessageSessionUid) {
        messageSessionUids.set(currentlyOpenedChatId, response.activeMessageSessionUid);
      }

      setHasWriteAccess(currentlyOpenedChatId, response.hasWriteAccess);
      dispatch(aiChatWindowActions.setIsDeleted({chatId: currentlyOpenedChatId, isDeleted: response.isDeleted ?? false}));
      dispatch(aiChatWindowActions.setSource({chatId: currentlyOpenedChatId, source: response.source ?? null}));
      actions.setSearchTagsForChat(currentlyOpenedChatId, response.searchTags ?? []);

      if (response.pendingSurvey) {
        raisePendingSurvey(currentlyOpenedChatId, response.pendingSurvey);
      }

      if (response.activeMessageSessionUid) {
        await handleRefreshedInProgressChat(response.activeMessageSessionUid);
      }
    } catch (e: any) {
      console.error('Failed to fetch chat history:', e);

      if (e?.status === 0) {
        handleConnectionIssue();
        return;
      }

      if (e?.code === CHAT_NOT_FOUND_ERROR_CODE) {
        handleChatNotFound();
        return;
      }
    } finally {
      setChatLoading(false);
      setIsRefreshing(currentlyOpenedChatId, false);
      setHistoryFetchComplete(true);
    }
  }, [
    currentlyOpenedChatId,
    isStreaming,
    messageSessionUids,
    handleRefreshedInProgressChat,
    raisePendingSurvey,
    setMessages,
    setHasWriteAccess,
    setIsRefreshing,
    dispatch,
    actions.setSearchTagsForChat,
  ]);

  useEffect(() => {
    const element = promptBoxRef.current;
    if (!element) {
      return;
    }

    setPromptBoxHeight(element.offsetHeight);

    const observer = new ResizeObserver((entries) => {
      const newHeight = entries[0].contentRect.height;
      setPromptBoxHeight(newHeight);
    });

    observer.observe(element);

    return (): void => {
      observer.unobserve(element);
    };
  }, [promptBoxRef]);

  useEffect(() => {
    if (!currentlyOpenedChatId) {
      setHistoryFetchComplete(true);
      return;
    }

    const hasCachedMessages = Object.keys(messages).length > 0;

    if (!hasCachedMessages) {
      void getChatHistory();
    } else {
      setHistoryFetchComplete(true);
    }
  }, [currentlyOpenedChatId, getChatHistory]);

  useEffect(() => {
    scrollToBottom(messagesRef.current, 'instant');
  }, [currentlyOpenedChatId, messagesRef]);

  useEffect(() => {
    const clearScrollTargetParam = (): void => {
      const url = new URL(window.location.href);
      url.searchParams.delete(MESSAGE_ID_PARAM);
      const cleanedUrl = serializeUrl(url).slice(url.origin.length);
      window.history.replaceState({path: cleanedUrl}, '', cleanedUrl);
    };

    const scrollToMessage = (): void => {
      const messageId = new URLSearchParams(window.location.search).get(MESSAGE_ID_PARAM);

      if (!messageId || !messagesRef.current || Object.keys(messages).length === 0) {
        return;
      }

      setTimeout(() => {
        if (!messagesRef.current) {
          return;
        }
        const messageElement = messagesRef.current.querySelector<HTMLElement>(`[data-message-id="${messageId}"]`);
        if (messageElement) {
          messageElement.scrollIntoView({behavior: 'smooth', block: 'center'});
          messageElement.style.backgroundColor = 'rgba(255, 255, 0, 0.2)';
          setTimeout(() => {
            messageElement.style.backgroundColor = '';
          }, 2000);
          clearScrollTargetParam();
        }
      }, 100);
    };

    if (currentlyOpenedChatId && Object.keys(messages).length > 0) {
      scrollToMessage();
    }
  }, [currentlyOpenedChatId, messages]);

  useEffect(() => {
    if (hasInitiatedSendOnLoadRef.current) {
      return;
    }
    if (!props.chatOnLoadParams?.sendOnLoad) {
      return;
    }
    if (!props.chatOnLoadParams.userRequestParts || props.chatOnLoadParams.userRequestParts.length === 0) {
      return;
    }
    if (!historyFetchComplete) {
      return;
    }

    hasInitiatedSendOnLoadRef.current = true;
    replaceHistoryOnChatCreationRef.current = props.chatOnLoadParams.replaceHistoryOnChatCreation ?? false;
    props.onChatOnLoadConsumed?.();

    const initializeChatOnLoad = async (chatOnLoadParams: ChatOnLoadParams): Promise<void> => {
      const userRequestParts = chatOnLoadParams.userRequestParts ?? [];
      const requestConfig = buildRequestConfig({requestInstruction: chatOnLoadParams.requestInstruction, preferences: chatOnLoadParams.preferences});
      await handleChatSent(userRequestParts, requestConfig);
    };

    void initializeChatOnLoad(props.chatOnLoadParams);
  }, [historyFetchComplete, messages, props.chatOnLoadParams?.sendOnLoad]);

  const onMessageRefMount = useCallback((container: HTMLDivElement): void => {
    if (!container) {
      return;
    }

    messagesRef.current = container;
  }, []);

  const buyCreditsAndResumeChat = async (userRequestParts: ChatMessagePart[], requestConfig?: RequestConfig): Promise<void> => {
    const purchaseData = await showPurchaseCreditsDialog();

    if (!purchaseData?.creditsV2) {
      return;
    }

    props.refreshCredits();

    if (purchaseData.creditsV2 >= (await getAiChatMessageCost())) {
      await startGenerationStream(currentlyOpenedChatId, userRequestParts, requestConfig);
    }
  };

  const handleOutOfCredits = useCallback(
    (userRequestParts: ChatMessagePart[], requestConfig?: RequestConfig): void => {
      if (isUserTrial()) {
        showConvertTrialForAiChat((): void => {
          void buyCreditsAndResumeChat(userRequestParts, requestConfig);
        });

        return;
      }

      if (!isPremiumUser) {
        showAiChatPremiumOnlyFeatureDialog(props.userHasChatHistory, getAiPromptBoxStateFromStore(props.id));
        return;
      }

      void buyCreditsAndResumeChat(userRequestParts, requestConfig);
    },
    [showPurchaseCreditsDialog, startGenerationStream, props.onSendMessage, props.id]
  );

  const handleAnonymousUserChatRequest = useCallback(
    async (userRequestParts: ChatMessagePart[], requestConfig?: RequestConfig): Promise<void> => {
      const isLoggedIn = await openAiChatAuthenticateModal(props.onLoginSuccessActions);
      if (!isLoggedIn) {
        return;
      }

      if (!isUserPremium() && !isUserPremiumPlus()) {
        showAiChatPremiumOnlyFeatureDialog(props.userHasChatHistory, getAiPromptBoxStateFromStore(props.id));
        return;
      }

      await refreshTeamCreditsInStore();
      const {credits} = await getTeamCredits();
      if (credits < (await getAiChatMessageCost())) {
        handleOutOfCredits(userRequestParts, requestConfig);
        return;
      }

      await startGenerationStream(currentlyOpenedChatId, userRequestParts, requestConfig);
    },
    [startGenerationStream, handleOutOfCredits, props.onSendMessage, props.id]
  );

  const handleChatSent = useCallback(
    async (userRequestParts: ChatMessagePart[], requestConfig?: RequestConfig): Promise<void> => {
      actions.setEditingUserMessageId(null);

      if (!currentlyOpenedChatId) {
        pinnedAspectRatioForChatBeingCreatedRef.current = pinnedAspectRatio;
        pinnedBrandKitIdForChatBeingCreatedRef.current = pinnedBrandKitId;
      }

      if (!props.isUserLoggedIn) {
        await handleAnonymousUserChatRequest(userRequestParts, requestConfig);
        return;
      }

      if (!isPremiumUser && !aiChatState.isPaygUserWithAiChatAccess) {
        showAiChatPremiumOnlyFeatureDialog(props.userHasChatHistory, getAiPromptBoxStateFromStore(props.id));
        return;
      }

      if (props.credits < (await getAiChatMessageCost())) {
        handleOutOfCredits(userRequestParts, requestConfig);
        return;
      }

      await startGenerationStream(currentlyOpenedChatId, userRequestParts, requestConfig);
    },
    [actions, startGenerationStream, handleOutOfCredits, props.onSendMessage, handleAnonymousUserChatRequest, currentlyOpenedChatId, pinnedAspectRatio, pinnedBrandKitId, props.id]
  );

  const handleSurveySubmit = useCallback(
    async (response: SurveyResponse, attachmentParts?: ChatMessagePart[]): Promise<void> => {
      if (!currentlyOpenedChatId) {
        return;
      }
      const submitted = activeSurveyByChatId[currentlyOpenedChatId];
      clearSurveyForChat(currentlyOpenedChatId);

      if (!submitted) {
        return;
      }

      clearPinnedAspectRatioOnSurveyAnswer(submitted.spec.surveyId, response);

      const answerParts: ChatMessagePart[] = [...createUserRequestParts(renderSurveyResponseEcho(submitted.spec, response), []), ...(attachmentParts ?? [])];
      const promptBoxState = getAiPromptBoxStateFromStore(props.id);
      const requestConfig = buildRequestConfig({
        preferences: buildRequestPreferences(promptBoxState?.pinnedAspectRatio ?? null, promptBoxState?.pinnedBrandKitId ?? null),
        surveyPicks: buildSurveyPicks(submitted.spec, response),
        answeredSteps: buildAnsweredFormSteps(submitted.spec, response),
      });

      await handleChatSent(answerParts, requestConfig);
    },
    [activeSurveyByChatId, clearPinnedAspectRatioOnSurveyAnswer, clearSurveyForChat, currentlyOpenedChatId, handleChatSent, props.id]
  );

  const handleRetry = useCallback(async (): Promise<void> => {
    const lastUserMessage = findLastUserMessage(messages);

    if (!lastUserMessage?.parts) {
      console.error('Retry failed: Could not find the last user message to retry.');
      return;
    }

    if (!currentlyOpenedChatId) {
      console.error('Retry failed: No current chat ID available.');
      return;
    }

    const requestConfig = buildRequestConfig({preferences: buildRequestPreferences(pinnedAspectRatio, pinnedBrandKitId)});

    await handleChatSent(lastUserMessage.parts, requestConfig);
  }, [messages, handleChatSent, pinnedAspectRatio, pinnedBrandKitId]);

  const generate = useCallback(
    async (prompt: string, promptAttachments?: AiPromptBoxAttachments, requestConfig?: RequestConfig, purpose?: TextPartPurpose): Promise<void> => {
      const userRequestParts = createUserRequestParts(prompt, promptAttachments?.items ?? [], purpose);

      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.AICHAT_START, {[GA4EventParamName.SOURCE]: AiChatStartSource.AI_HOMEPAGE});

      await handleChatSent(userRequestParts, requestConfig);
    },
    [handleChatSent]
  );

  const handleRegenerate = (prompt?: string, requestConfig?: RequestConfig, purpose?: TextPartPurpose): void => {
    const promptToUse = prompt ?? window.i18next.t('pmwjs_make_another_version');
    void generate(promptToUse, undefined, requestConfig, purpose);
  };

  const handleEditSend = useCallback(
    async (editedMessageId: string, newText: string, removedDocumentHashedIds: string[]): Promise<void> => {
      if (!currentlyOpenedChatId) {
        return;
      }
      if (currentlyOpenedChatId) {
        setEditSendInProgressMessageKeyMap((prev) => ({...prev, [currentlyOpenedChatId]: editedMessageId}));
      }

      const messageKeys = Object.keys(messages);
      const editedIndex = messageKeys.indexOf(editedMessageId);

      if (editedIndex === -1) {
        return;
      }

      const previousMessageId = editedIndex > 0 ? messageKeys[editedIndex - 1] : undefined;
      const originalMessage = messages[editedMessageId];
      const userRequestParts = buildEditResendParts(newText, originalMessage?.parts ?? [], removedDocumentHashedIds);
      try {
        actions.setEditingUserMessageId(null);
        await startGenerationStream(currentlyOpenedChatId, userRequestParts, undefined, {
          editedMessageId,
          previousMessageId,
        });
      } catch {
        if (currentlyOpenedChatId) {
          setEditSendInProgressMessageKeyMap((prev) => ({...prev, [currentlyOpenedChatId]: null}));
        }
        actions.setEditingUserMessageId(null);
        showMessageGrowl({
          text: window.i18next.t('pmwjs_failed_to_send_edited_message'),
          key: 'failedToSendEditedMessage',
          interval: 2000,
          showCloseIcon: true,
          type: GROWL_TYPE.DANGER,
        });
      }
    },
    [currentlyOpenedChatId, startGenerationStream, actions, messages]
  );

  const onStop = async (): Promise<void> => {
    if (!currentlyOpenedChatId) {
      return;
    }

    setIsRefreshing(currentlyOpenedChatId, false);
    scrollToBottom(messagesRef.current);

    await stopGeneration(currentlyOpenedChatId);
  };

  const handleReattach = useCallback(
    (part: ImagePart, previewUrl?: string): void => {
      const attachment = convertImagePartToPromptAttachment(part, previewUrl);
      if (!attachment) {
        return;
      }

      dispatch(aiPromptBoxActions.addAttachments({id: props.id, attachments: [attachment]}));
      dispatch(aiPromptBoxActions.updateAiPromptBox({id: props.id, value: {promptBoxState: PromptBoxState.ACTIVE}}));
    },
    [dispatch, props.id]
  );

  const getBubbleClassNames = (): string => {
    const classes = [styles.bubble];

    if (isSmall) {
      classes.push(styles.bubbleSmall);
    }

    if (isMedium) {
      classes.push(styles.bubbleMedium);
    }

    return classes.join(' ');
  };

  const getPromptBoxPositioningClassNames = (): string => {
    if (isMedium) {
      return `${styles.promptBoxBottomPositioning} ${styles.promptBoxBottomPositioningMedium}`;
    }

    return styles.promptBoxBottomPositioning;
  };

  const renderFloatingPromptBox = (): ReactElement => {
    const isPromptBoxInvisible = chatLoading || !hasWriteAccess || isDeleted;

    if (activeSurvey) {
      return (
        <div
          ref={promptBoxRef}
          className={`${props.hasFloatingPromptBox ? '_position-absolute' : ''} _full-width ${getPromptBoxPositioningClassNames()} _visible`}
        >
          <div className={styles.promptBoxInset}>
            <Survey key={activeSurvey.spec.surveyId} spec={activeSurvey.spec} onSubmit={handleSurveySubmit} onCancel={handleSurveyCancel} />
          </div>
        </div>
      );
    }

    return (
      <div
        ref={promptBoxRef}
        className={`${props.hasFloatingPromptBox ? '_position-absolute' : ''} _full-width ${getPromptBoxPositioningClassNames()} ${isPromptBoxInvisible ? '_invisible' : '_visible'}`}
      >
        <AiChatWindowPromptBox
          shouldShowScrollToBottomButton={!chatLoading}
          containerClassname={`${styles.promptBoxContainer} ${currentlyOpenedChatId ? styles.hasMessages : ''}`}
          isMessageInProgress={isStreaming || isRefreshing}
          hasChatHistory={Object.keys(messages).length > 0}
          chatId={currentlyOpenedChatId}
          messages={messages}
          id={props.id}
          onStopGeneration={onStop}
          onGenerate={generate}
          onRegenerate={handleRegenerate}
          showProgressiveBlur={!!currentlyOpenedChatId}
          initialPrompt={initialPrompt}
          initialAttachments={initialAttachments}
          initialPinnedAspectRatio={currentlyOpenedChatId ? pinnedAspectRatioForChatBeingCreatedRef.current : onLoadPinnedAspectRatio}
          initialPinnedBrandKitId={currentlyOpenedChatId ? pinnedBrandKitIdForChatBeingCreatedRef.current : onLoadPinnedBrandKitId}
        />
      </div>
    );
  };

  const renderMessages = (): ReactElement => {
    if (chatLoading) {
      return (
        <div className={`_full-width _full-height flex-center ${styles.loaderContainer}`}>
          <CircularProgressLoader color={LOADER_COLOR.AI_DARK} classes={'spacing-m-l-0'} />
        </div>
      );
    }

    return (
      <ChatMessages
        key={currentlyOpenedChatId}
        heightAdjustmentForFloatingPromptBox={props.hasFloatingPromptBox ? promptBoxHeight : undefined}
        isMessageStreaming={isStreaming}
        waitingForRefreshedChatToFinish={isRefreshing}
        messages={messages}
        chatId={currentlyOpenedChatId}
        bubbleClassNames={getBubbleClassNames()}
        onRetry={handleRetry}
        onMessageRefMount={onMessageRefMount}
        onRegenerate={handleRegenerate}
        onGenerate={generate}
        onReattach={handleReattach}
        onEditSend={handleEditSend}
        editSendInProgressMessageKey={editSendInProgressMessageKey}
      />
    );
  };

  const getDeletedChatTagForSuperUser = (): ReactNode => {
    if (isDeleted) {
      return (
        <div className={`spacing-p-4 flexbox flex-center gap-2`}>
          <Icon className={`content-danger`} icon="icon-delete" size={IconSize.SIZE_ICON_32} type={IconType.DANGER} shape={IconShape.NONE} />
          <Text val={window.i18next.t('pmwjs_chat_has_been_deleted')} className={'content-danger'} size={TextSize.XLARGE} />
        </div>
      );
    }

    return null;
  };

  const getContent = (): ReactElement => {
    return (
      <>
        {renderMessages()}
        {renderFloatingPromptBox()}
        {getDeletedChatTagForSuperUser()}
      </>
    );
  };

  const getContentWrapperClassNames = (): string => {
    if (isDeleted) {
      return styles.deletedChat;
    }

    if (!hasWriteAccess) {
      return styles.readOnlyAccessChat;
    }

    return '';
  };

  return (
    <div className={`_position-relative ${styles.shell}`}>
      <div className={`${styles.stage} ${styles.withMessages} ${getContentWrapperClassNames()}`}>{getContent()}</div>
    </div>
  );
}
